import { parseDate, dayDiff } from "../helpers/date_helpers"
import { DayStrategy } from "../periods/day_strategy"

export class RangeFieldsModel {
  constructor(attrs) {
    this.mode        = attrs.mode
    this.period      = attrs.period
    this.comparison  = attrs.comparison
    this.presetData  = attrs.presetData
    this.grouping    = attrs.grouping
    this.followCount = attrs.followCount
    this.leaderRange = attrs.leaderRange
  }

  /* Modes */
  /* ------------------------------------------------------------------------ */

  get inCustomMode() { return this.mode == 'custom' }
  get inPresetMode() { return this.mode == 'preset' }

  /* ------------------------------------------------------------------------ */

  get shouldEnableYearOption() {
    if (this.inCustomMode) {
      return !this.leaderRangeTooLongForYearComparison
    } else {
      return this.selectedPeriodIsYearComparable
    }
  }

  get shouldDisplayComparisons() {
    if (this.followCount == 0) { return false }

    if (this.inCustomMode) {
      return !this.leaderRangeTooLongForPeriodComparison
    } else {
      return this.selectedPeriodIsComparable
    }
  }

  get shouldDisplayAddComparison() {
    if (this.followCount > 0) { return false }

    if (this.inCustomMode) {
      return !this.leaderRangeTooLongForPeriodComparison
    } else {
      return this.selectedPeriodIsComparable
    }
  }

  get shouldDisplayFollowRow() {
    return this.followCount > 0
  }

  get shouldDisplayInfo() {
    return !this.shouldDisplayComparisons
  }

  /* Leader Range */
  /* ------------------------------------------------------------------------ */

  get leaderDateMin() { return this.leaderRange[0] }
  get leaderDateMax() { return this.leaderRange[1] }

  get leaderRangeDuration() {
    if (this.leaderRange.every((dt) => dt.isValid)) {
      return dayDiff(this.leaderDateMin, this.leaderDateMax)
    }
  }

  get leaderRangeDays() {
    return this.leaderRangeDuration?.days
  }

  get leaderRangeTooLongForYearComparison() {
    return this.leaderRangeDays > this.yearComparabilityMax
  }

  get leaderRangeTooLongForPeriodComparison() {
    return this.leaderRangeDays > this.periodComparabilityMax
  }

  /* Preset Data */
  /* ------------------------------------------------------------------------ */

  get selectedPeriodData() {
    return this.presetData?.map?.[this.period]
  }

  get selectedPeriodRanges() {
    if (this.inCustomMode && this.period === 'custom') {
      return this.customComparisonRanges
    }

    return this.selectedPeriodData?.[this.comparison]
  }

  get customComparisonRanges() {
    if (!this.leaderRangeIsComparable) return

    const strategy = new DayStrategy()
    strategy.period = 'custom'
    strategy.range = this.leaderRange
    const compared = strategy.compare(this.comparison)
    if (!compared) return

    const [min, max] = compared.range
    if (!min || !max) return

    return { 2: { min, max } }
  }

  get leaderRangeIsComparable() {
    const [start, end] = this.leaderRange || []
    if (!start?.isValid || !end?.isValid) return false

    return end >= start
  }

  get suggestedUnit() {
    return this.selectedPeriodData?.unit
  }

  get comparability() {
    return this.selectedPeriodData?.comparability
  }

  get selectedPeriodIsComparable() {
    return this.comparability?.year || this.comparability?.period
  }

  get selectedPeriodIsYearComparable() {
    return this.comparability?.year
  }

  get selectedPeriodIsPeriodComparable() {
    return this.comparability?.period
  }

  get selectedPeriodIsFullyComparable() {
    return this.comparability?.year && this.comparability?.period
  }

  get yearComparabilityMax() {
    return this.presetData?.comparability?.year_max || 365
  }

  get periodComparabilityMax() {
    return this.presetData?.comparability?.period_max
  }
}
